import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { Textarea } from '../../../../components/forms/Textarea';
import { SchedulingProfile } from '../types';
import { useProfileSave } from '../useProfileSave';

const REMINDER_TEXT_MAX_LENGTH = 320;

export const NotificationsTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t } = useTranslation();
  const { save, isSaving, fieldErrors, formError } = useProfileSave(profile.id);
  const [sendSms, setSendSms] = useState(profile.send_confirmation_sms);
  const [reminder24h, setReminder24h] = useState(profile.reminder_24h_enabled);
  const [reminder1h, setReminder1h] = useState(profile.reminder_1h_enabled);
  const [textDe, setTextDe] = useState(profile.reminder_text_de);
  const [textEn, setTextEn] = useState(profile.reminder_text_en);

  const unchanged =
    sendSms === profile.send_confirmation_sms &&
    reminder24h === profile.reminder_24h_enabled &&
    reminder1h === profile.reminder_1h_enabled &&
    textDe === profile.reminder_text_de &&
    textEn === profile.reminder_text_en;

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('schedulingAdmin.notifications.intro')}
      </p>
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('schedulingAdmin.notifications.confirmationTitle')}
        </h3>
        <Checkbox
          label={t('schedulingAdmin.notifications.confirmationSms')}
          helperText={t('schedulingAdmin.notifications.confirmationSmsHelp')}
          checked={sendSms}
          onChange={e => setSendSms(e.target.checked)}
        />
      </div>      <div className="space-y-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('schedulingAdmin.notifications.remindersTitle')}
        </h3>
        <Checkbox
          label={t('schedulingAdmin.notifications.reminder24h')}
          checked={reminder24h}
          onChange={e => setReminder24h(e.target.checked)}
        />
        <Checkbox
          label={t('schedulingAdmin.notifications.reminder1h')}
          checked={reminder1h}
          onChange={e => setReminder1h(e.target.checked)}
        />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('schedulingAdmin.notifications.reminderTextHelp')}
        </p>
        <Textarea
          label={t('schedulingAdmin.notifications.reminderTextDe')}
          value={textDe}
          onChange={e => setTextDe(e.target.value)}
          maxLength={REMINDER_TEXT_MAX_LENGTH}
          showCount
          minRows={2}
          fullWidth
          errorText={fieldErrors.reminder_text_de}
        />
        <Textarea
          label={t('schedulingAdmin.notifications.reminderTextEn')}
          value={textEn}
          onChange={e => setTextEn(e.target.value)}
          maxLength={REMINDER_TEXT_MAX_LENGTH}
          showCount
          minRows={2}
          fullWidth
          errorText={fieldErrors.reminder_text_en}
        />
      </div>

      {formError && <p className="text-sm text-red-600">{formError}</p>}
      <Button
        onClick={() =>
          save({
            send_confirmation_sms: sendSms,
            reminder_24h_enabled: reminder24h,
            reminder_1h_enabled: reminder1h,
            reminder_text_de: textDe,
            reminder_text_en: textEn,
          })
        }
        loading={isSaving}
        disabled={unchanged}
      >
        {t('common.save')}
      </Button>
    </div>
  );
};
